﻿import { FieldDefinition, isHidden } from '@/shared/codeedit/field.ts';
import { AttributeValue, CodeEditFields } from '@/shared/codeedit/codeEditFields.ts';

export type ChangeDocumentRequestCode = Record<string, AttributeValue>;

export type ChangeDocumentRequest = {
    codeSetName: string;
    codes: ChangeDocumentRequestCode[];
};

export function capitalize(s: string): string {
    return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Takes the code from the edit session and creates the change document request code that is sent to the API.
 * @throws {Error} If modified has an undefined value for any field in fieldDefinitions, or original,
 * when provided, has an undefined value for a field needed for comparison. Built-in, hidden, and
 * identity fields are not read from original. Null values are allowed.
 */
export function createChangeDocumentCode(
    fieldDefinitions: FieldDefinition[],
    modified: CodeEditFields,
    original: CodeEditFields | null = null): ChangeDocumentRequestCode {
    const code: Record<string, AttributeValue> = {};

    for (const fieldDefinition of fieldDefinitions) {
        const modifiedValue = modified[fieldDefinition.field];
        if (modifiedValue === undefined) {
            throw new Error(`modified value for attribute ${fieldDefinition.field} is undefined`);
        }

        if (['name', 'description', 'isValid'].includes(fieldDefinition.field)) {
            code[capitalize(fieldDefinition.field)] = modifiedValue;
            continue;
        }

        if (isHidden(fieldDefinition)) {
            continue;
        }

        if (fieldDefinition.includeInIdentity) {
            // always include identity values
            code[fieldDefinition.field] = modifiedValue;
            continue;
        }

        if (!original) {
            // new code
            if (!fieldDefinition.attribute) {
                // non-attribute values are taken at face value, even if they are null
                code[fieldDefinition.field] = modifiedValue;
            } else if (modifiedValue !== null) {
                // attribute values are added if not null
                code[fieldDefinition.field] = modifiedValue;
            }
        } else {
            // existing code
            const originalValue = original[fieldDefinition.field];
            if (originalValue === undefined) {
                throw new Error(`original value for attribute ${fieldDefinition.field} is undefined`);
            }
            if (!fieldDefinition.attribute) {
                if (modifiedValue !== originalValue) {
                    // add changed properties as-is if they are changed
                    code[fieldDefinition.field] = modifiedValue;
                }
            } else if (modifiedValue === null && originalValue !== null) {
                // delete symbol if changed to null
                code[fieldDefinition.field] = '{null}';
            } else if (modifiedValue !== originalValue) {
                // add changed attributes
                code[fieldDefinition.field] = modifiedValue;
            }
        }
    }

    return code;
}

/**
 * Creates a change document containing the code's identity fields.
 * @throws {Error} If the provided CodeEditFields object (newCode) is missing a field
 * defined in the provided fieldDefinitions.
 */
export function createIdentityChangeDocument(
    newCode: CodeEditFields,
    fieldDefinitions: FieldDefinition[]): ChangeDocumentRequestCode {
    const code: ChangeDocumentRequestCode = {};
    for (const attr of fieldDefinitions) {
        if (attr.includeInIdentity && attr.field !== 'identity') {
            const fieldValue = newCode[attr.field];
            if (fieldValue === undefined) {
                throw new Error(`fieldValue for attribute ${attr.field} is undefined`);
            }
            if (['name', 'description', 'isValid'].includes(attr.field))
                code[capitalize(attr.field)] = fieldValue;
            else if (fieldValue === '' || fieldValue === null)
                continue;
            else
                code[attr.field] = fieldValue;
        }
    }
    return code;
}
